import React,{useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiPhone,FiMail,FiMapPin,FiClock,FiSend,FiUser,FiMessageSquare}=FiIcons;

const Contact=()=> {
  const [formData,setFormData]=useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange=(e)=> {
    const {name,value}=e.target;
    setFormData(prev=> ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit=(e)=> {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:',formData);
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    alert('Thank you for your message! We will get back to you soon.');
  };

  const contactInfo=[
    {icon: FiPhone,title: 'Phone',details: '+1 (647) 616-3415',link: 'tel:+16476163415'},
    {icon: FiMail,title: 'Email',details: 'info@annijewelry.com',link: 'mailto:info@annijewelry.com'},
    {icon: FiMapPin,title: 'Address',details: '107 Church St UNIT # 500 /5 floor,Toronto,ON M5C 1P1,Canada',link: 'https://maps.app.goo.gl/hhXo7X7wt7sgjcqU7'},
    {icon: FiClock,title: 'Hours',details: 'Mon-Sat: 10:00 AM - 7:00 PM\nSun: 12:00 PM - 5:00 PM',link: null}
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-black via-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{opacity: 0,y: 30}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            className="text-5xl md:text-6xl font-bold text-white mb-6 font-playfair"
          >
            Contact <span className="text-gold">Us</span>
          </motion.h1>
          <motion.p
            initial={{opacity: 0,y: 30}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.8,delay: 0.2}}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            We&apos;re here to help you find the perfect piece or answer any questions you may have. Get in touch with our team of jewelry experts today.
          </motion.p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {contactInfo.map((info,index)=> (
              <motion.div
                key={index}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                transition={{duration: 0.8,delay: index * 0.1}}
                viewport={{once: true}}
                className="text-center p-6 bg-gray-900 rounded-lg border border-gold/20 hover:border-gold/50 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={info.icon} className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-semibold text-gold mb-3">{info.title}</h3>
                {info.link ? (
                  <a
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : '_self'}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="text-gray-300 hover:text-gold transition-colors duration-300 whitespace-pre-line"
                  >
                    {info.details}
                  </a>
                ) : (
                  <p className="text-gray-300 whitespace-pre-line">{info.details}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form and Map */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{opacity: 0,x: -30}}
              whileInView={{opacity: 1,x: 0}}
              transition={{duration: 0.8}}
              viewport={{once: true}}
            >
              <h2 className="text-4xl font-bold text-white mb-6 font-playfair">
                Send us a <span className="text-gold">Message</span>
              </h2>
              <p className="text-gray-300 mb-8">
                Have a question about our jewelry or services? Fill out the form below and we&apos;ll get back to you as soon as possible.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-gold font-medium mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gold w-5 h-5" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-12 pr-4 py-3 bg-black border border-gold/20 rounded-lg text-white placeholder-gray-400 focus:border-gold focus:outline-none transition-colors duration-300"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gold font-medium mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gold w-5 h-5" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-12 pr-4 py-3 bg-black border border-gold/20 rounded-lg text-white placeholder-gray-400 focus:border-gold focus:outline-none transition-colors duration-300"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-gold font-medium mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiPhone} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gold w-5 h-5" />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full pl-12 pr-4 py-3 bg-black border border-gold/20 rounded-lg text-white placeholder-gray-400 focus:border-gold focus:outline-none transition-colors duration-300"
                        placeholder="(123) 456-7890"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-gold font-medium mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gold/20 rounded-lg text-white focus:border-gold focus:outline-none transition-colors duration-300"
                    >
                      <option value="">Select a subject</option>
                      <option value="custom-design">Custom Design</option>
                      <option value="engagement-ring">Engagement Ring</option>
                      <option value="repair">Jewelry Repair</option>
                      <option value="appraisal">Appraisal Service</option>
                      <option value="general">General Inquiry</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-gold font-medium mb-2">
                    Message *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiMessageSquare} className="absolute left-3 top-4 text-gold w-5 h-5" />
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full pl-12 pr-4 py-3 bg-black border border-gold/20 rounded-lg text-white placeholder-gray-400 focus:border-gold focus:outline-none transition-colors duration-300 resize-none"
                      placeholder="Tell us about your jewelry needs or questions..."
                    ></textarea>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gold text-black py-4 px-6 rounded-lg font-semibold text-lg hover:bg-yellow-400 transition-all duration-300 flex items-center justify-center shadow-gold"
                >
                  <SafeIcon icon={FiSend} className="w-5 h-5 mr-2" />
                  Send Message
                </button>
              </form>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{opacity: 0,x: 30}}
              whileInView={{opacity: 1,x: 0}}
              transition={{duration: 0.8}}
              viewport={{once: true}}
            >
              <h2 className="text-4xl font-bold text-white mb-6 font-playfair">
                Visit Our <span className="text-gold">Store</span>
              </h2>
              <p className="text-gray-300 mb-8">
                Located in the heart of Toronto,our showroom features our complete collection of fine jewelry. Come visit us for a personalized consultation.
              </p>

              <div className="bg-black p-4 rounded-lg border border-gold/20">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2886.7831314895884!2d-79.3755498!3d43.6526802!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d4cba0fe4c0d6d%3A0x31b3ce253922a2e0!2sAnni%20Jewelry!5e0!3m2!1sen!2s!4v1764768900954!5m2!1sen!2s"
                  width="100%"
                  height="400"
                  style={{border: 0}}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-lg"
                ></iframe>
              </div>

              <div className="mt-8 p-6 bg-black rounded-lg border border-gold/20">
                <h3 className="text-xl font-bold text-gold mb-4">Store Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 text-gold mr-3 flex-shrink-0" />
                    <span className="text-gray-300">107 Church St UNIT # 500 /5 floor</span>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-gold mr-3 flex-shrink-0" />
                    <span className="text-gray-300">Mon-Sat: 10:00 AM - 7:00 PM</span>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiPhone} className="w-5 h-5 text-gold mr-3 flex-shrink-0" />
                    <a href="tel:+16476163415" className="text-gray-300 hover:text-gold transition-colors duration-300">
                      +1 (647) 616-3415
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-playfair">
              Frequently Asked <span className="text-gold">Questions</span>
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {question: "Do you offer custom jewelry design?",answer: "Yes! We specialize in custom jewelry design. Our expert designers work with you to create unique pieces that reflect your personal style and story."},
              {question: "What is your return policy?",answer: "We offer a 30-day return policy for most items in original condition. Custom pieces have different terms which we'll discuss during consultation."},
              {question: "Do you provide jewelry appraisals?",answer: "Yes,we offer certified jewelry appraisals for insurance purposes,estate planning,or personal knowledge. Our certified appraisers provide detailed documentation."},
              {question: "How long does custom jewelry take?",answer: "Custom pieces typically take 3-6 weeks depending on complexity. We'll provide a detailed timeline during your consultation."}
            ].map((faq,index)=> (
              <motion.div
                key={index}
                initial={{opacity: 0,y: 20}}
                whileInView={{opacity: 1,y: 0}}
                transition={{duration: 0.8,delay: index * 0.1}}
                viewport={{once: true}}
                className="bg-gray-900 p-6 rounded-lg border border-gold/20"
              >
                <h3 className="text-lg font-semibold text-gold mb-3">{faq.question}</h3>
                <p className="text-gray-300">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;